import { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as _ from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import MatchCard from 'components/general/match_card.js';
import { getSportTypeToActiveIcon, getSportTypeToWhiteIcon } from 'toolkit/utils';
import GoBack from 'components/general/go_back';
import SportDetailItem from 'components/SportDetailItem.js';
import { fetchLiveMatches, setNextLiveMatches, setSelectedSport } from 'stores/match';
import { eventsToGroup } from 'toolkit/eventsToGroup.js';
import NextLiveMatches from './NextLiveMatches';
import { AWS_BUCKET_URL } from 'toolkit/constants';

function SportDetail() {
  const matchCardListRef = useRef(null);
  const dispatch = useDispatch();
  const selectedSport = useSelector((state) => state.match.selectedSport);
  const selectedMatch = useSelector((state) => state.match.selectedMatch);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const events = useSelector((state) => state.match.events);
  const liveEvents = useSelector((state) => {
    const { liveMatches, events } = state.match;
    return liveMatches.map((c) => events[c]);
  }, shallowEqual);
  const { t } = useTranslation('translations', { useSuspense: false });
  const [lastScrollPosition, setLastScrollPosition] = useState(
    localStorage.getItem('scrollPosition') || 0
  );
  const [sports, setSports] = useState({});
  const [sport, setSport] = useState('0');
  const [selectedMarketType, setSelectedMarketType] = useState(7);
  const location = useLocation();

  const handleLocationScroll = () => {
    const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
    setLastScrollPosition(currentPosition);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleLocationScroll);

    return () => {
      window.removeEventListener('scroll', handleLocationScroll);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('scrollPosition', lastScrollPosition.toString());
  }, [lastScrollPosition]);

  // useEffect(() => {
  //   const savedScrollPosition = localStorage.getItem('scrollPosition');

  //   if (savedScrollPosition) {
  //     window.scrollTo(0, parseInt(savedScrollPosition));
  //   }
  // }, []);

  const getData = async () => {
    try {
      // setLoading(true);
      // await dispatch(fetchLiveMatches()).unwrap();
      dispatch(fetchLiveMatches());
    } catch (error) {
      // error
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    // if (loading) return;

    if (selectedMatch) {
      const selectedMatchCard = document.querySelector(`a[href*="${selectedMatch}"]`);
      if (selectedMatchCard) {
        selectedMatchCard.scrollIntoView({ block: 'center' });
      }
    }
  }, []);

  useEffect(() => {
    const groups = eventsToGroup(liveEvents);
    setSports(groups);
    if (sport === '' && liveEvents.length > 0) {
      const type =
        selectedSport !== null || sports.hasOwnProperty(Number.parseInt(selectedSport)) === true
          ? selectedSport
          : Object.keys(groups)[0];

      setSport(type);
    }
  }, [sport, liveEvents]);

  useEffect(() => {
    if (sport !== '') {
      dispatch(setSelectedSport(sport));
    }
  }, [sport]);

  const selectSport = useCallback((id) => {
    setSport(id);
    dispatch(setNextLiveMatches());
  });

  const handleScroll = () => {
    const container = window;
    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const scrollTop = container.scrollY;
    const direction = scrollTop < lastScrollPosition ? 'up' : 'down';
    setLastScrollPosition(scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }
  };

  if (window) {
    window.removeEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
  }

  function calculateMenuItemsDynamic() {
    const menuItems = [];
    let icon = '';
    Object.keys(sports).map((sportType, index) => {
      if (sportType === sport) {
        icon = getSportTypeToActiveIcon(sport);
      } else {
        icon = getSportTypeToActiveIcon(sportType);
      }
      if (sports[sportType].length > 0) {
        menuItems.push(
          <SportDetailItem
            key={index}
            active={sport === sportType}
            count={_.sum(sports[sportType].map((l) => l.matches.length))}
            icon={icon}
            onClick={() => {
              selectSport(sportType);
            }}
            text={t('sports.' + sportType)}
            className={'d-flex flex-column p-2 fs-9'}
          />
        );
      }
    });

    return menuItems;
  }

  function getBetType() {
    const betTypes = [
      {
        label: t('rom'),
        id: 7
      },
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      }
    ];
    return (
      <div className="tip-types">
        {betTypes.map((betType) => (
          <div className="d-inline-block w-25 fw-bold " key={betType.id}>
            <button
              className={`btn btn-sm w-100 border fs-8 ${selectedMarketType === betType.id ? 'btn-primary fw-bold text-white' : 'btn-light'
                }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function getBets() {
    const matchCards = [];
    const favoriteMatchIds = new Set();
    const favoriteMatchesByLeague = {};

    // Collect all favorite matches grouped by league
    if (sports[sport] && sports[sport].length > 0) {
      sports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch) => {
          liveMatch.matches.forEach((match) => {
            if (events[match.providerId]?.isFavorite) {
              favoriteMatchIds.add(match.providerId);
              const leagueKey = liveMatch.category + ' / ' + liveMatch.league;
              if (!favoriteMatchesByLeague[leagueKey]) {
                favoriteMatchesByLeague[leagueKey] = {
                  category: liveMatch.category,
                  league: liveMatch.league,
                  categoryId: match.categoryId,
                  matches: []
                };
              }
              favoriteMatchesByLeague[leagueKey].matches.push(match);
            }
          });
        });
    }

    // Display favorites grouped by league at the top
    Object.keys(favoriteMatchesByLeague).forEach((leagueKey) => {
      const favoriteGroup = favoriteMatchesByLeague[leagueKey];
      if (favoriteGroup.matches.length > 0) {
        let code = null;
        if (favoriteGroup.categoryId) {
          code = favoriteGroup.categoryId.split(':')[2];
        }

        matchCards.push(
          <Fragment key={'favorite-' + leagueKey}>
            <div className="text-start my-1 fw-bold fs-8 d-flex align-items-center">
              {code && (
                <img
                  width="20"
                  height="13"
                  src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                  alt={code}
                  className="me-2"
                />
              )}
              {favoriteGroup.category + ' / ' + favoriteGroup.league}
            </div>
            {favoriteGroup.matches.map((match, index) => (
              <MatchCard
                key={match.providerId}
                providerId={match.providerId}
                marketType={selectedMarketType}
                index={index}
                sport={sport}
                path={location.pathname}
                locales={locales}
                selectedLocale={selectedLocale}
              />
            ))}
          </Fragment>
        );
      }
    });

    // Display regular live matches (excluding favorites)
    if (sports[sport] && sports[sport].length > 0) {
      sports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch) => {
          // Filter out favorites
          const nonFavoriteMatches = liveMatch.matches.filter(
            (match) => !favoriteMatchIds.has(match.providerId)
          );

          if (nonFavoriteMatches.length === 0) return;

          let code = null;
          if (nonFavoriteMatches && nonFavoriteMatches.length > 0 && nonFavoriteMatches[0].categoryId) {
            code = nonFavoriteMatches[0].categoryId.split(':')[2];
          }
          matchCards.push(
            <Fragment key={nonFavoriteMatches[0]?.providerId || liveMatch.matches[0].providerId}>
              <div className="text-start my-1 fw-bold fs-8 d-flex align-items-center">
                {code && (
                  <img
                    width="20"
                    height="13"
                    src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                    alt={code}
                    className="me-2"
                  />
                )}
                {liveMatch.category + ' / ' + liveMatch.league}
              </div>
              {nonFavoriteMatches?.map((match, index) => (
                <MatchCard
                  key={match.providerId}
                  providerId={match.providerId}
                  marketType={selectedMarketType}
                  index={index}
                  sport={sport}
                  path={location.pathname}
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              ))}
            </Fragment>
          );
        });
    }

    // Check if there are no matches to display
    if (matchCards.length === 0) {
      return null;
    }

    return matchCards;
  }

  // if (loading) return <Loading message={t('search.loadingMatches')} />;

  return (
    <div className="container-fluid scrollable-y">
      <div
        className="overflow-auto text-nowrap bg-dark mx-n3 d-flex align-items-center justify-content-between px-2"
        style={{ minHeight: '36px', paddingTop: '4px', paddingBottom: '4px' }}>
        <div
          style={{
            flexShrink: 0,
            padding: '2px 4px',
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'}>
          <div style={{ transform: 'scale(0.7)', filter: 'invert(1) brightness(2)' }}>
            <GoBack />
          </div>
        </div>
        <div className="d-flex flex-nowrap overflow-auto gap-0" style={{ flex: 1, minWidth: 0, marginLeft: '4px' }}>
          {calculateMenuItemsDynamic()}
        </div>
      </div>
      {sport !== '0' && <div style={{ height: '8px' }} />}
      {sport === '0' && (
        <Row className="">
          <Col className="tip-types-container">{getBetType()}</Col>
        </Row>
      )}
      <div className="mt-n2" ref={matchCardListRef}>
        <div key={sport}>{getBets()}</div>
      </div>
      <NextLiveMatches sport={sport} selectedMarketType={selectedMarketType} />
    </div>
  );
}

export default SportDetail;
